import { Injectable } from '@angular/core';

import { from, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { ActionSheetController } from '@ionic/angular';

import { NavigationService } from '@core/services/navigation/navigation.service';
import { NewCardService } from '@core/services/new-products/new-cards/new-card.service';
import { ProductsService } from '@core/services/products/products.service';
import { DocumentSettingsUtilService } from '@core/services/utils/document-settings-util.service';

import { AdditionalCardService } from '@pages/product/additional-card/_services/additional-card.service';

import { BaseProductCommand } from '@shared/commands/base-product.command';
import { IProductCommandContext } from '@shared/models/command';
import { ProductTypes } from '@shared/models/products.model';
import { CommandAvailabilityService } from '@shared/services/command-availability.service';

@Injectable({ providedIn: 'root' })
export class AdditionalCardCommand extends BaseProductCommand {
  readonly type = 'additional';
  readonly title = 'Доп. карта';
  readonly icon = 'card';
  readonly productType = ProductTypes.CARD;
  readonly isInfoAction = false;
  readonly settingsNames = ['REQUEST/OPEN_ADDIT_CARD'];
  readonly order = 5; // Пятый в actions для card

  constructor(
    private actionSheetCtrl: ActionSheetController,
    private navigationService: NavigationService,
    private additionalCardService: AdditionalCardService,
    private productsService: ProductsService,
    private newCardService: NewCardService,
    commandAvailabilityService: CommandAvailabilityService
  ) {
    super(commandAvailabilityService);
  }

  async execute(context: IProductCommandContext): Promise<void> {
    this.additionalCardService
      .openAdditionalCard()
      .pipe(
        switchMap((settings) => {
          const enabledThirdPerson = DocumentSettingsUtilService.getBooleanSettingValueByKey(
            settings,
            'SETTING_THIRD_PERSON/USE_THIRD_PERSON'
          );

          const buttons = [
            { text: 'На себя', role: 'current' },
            { text: 'Отмена', role: 'cancel' }
          ];

          if (enabledThirdPerson) {
            buttons.splice(1, 0, { text: 'На третье лицо', role: 'another' });
          }

          return from(this.actionSheetCtrl.create({ header: 'Заказ карты', buttons }));
        }),
        tap((actionSheet) => actionSheet.present()),
        switchMap((actionSheet) => from(actionSheet.onWillDismiss()))
      )
      .subscribe((detail) => {
        if (detail.role !== 'cancel') {
          this.navigationService.push(`product/${context.data.id}/additional-card`, {
            state: { type: detail.role }
          });
        }
      });
  }

  protected checkAdditionalAvailability(context: IProductCommandContext): Observable<boolean> | boolean {
    // Получаем данные карты как в оригинальном компоненте (product.component.original.ts строки 194-205)
    return this.newCardService.getCards().pipe(
      map((cards) => {
        const cardData = cards.filter((card) => card.code === context.data.productCode)[0];

        // Проверяем возможность создания дополнительной карты с реальными данными карты
        return this.productsService.checkProductOnAdditionalCard(context.data, cardData);
      })
    );
  }
}
